import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaTrash, 
  FaImages,
  FaChartLine,
  FaEye,
  FaCalendarAlt
} from "react-icons/fa";
import { 
  FiSearch, 
  FiRefreshCw, 
  FiX, 
  FiGrid, 
  FiList,
  FiTrendingUp,
  FiClock
} from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import Swal from "sweetalert2";

const PlanList = () => {
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const plansPerPage = 10;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/get_all_plans`
        );
        const data = await response.json();
        setPlans(data.data || []);
        setFilteredPlans(data.data || []);
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchTerm) {
      const filtered = plans.filter(plan =>
        plan.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPlans(filtered);
      setCurrentPage(1);
    } else {
      setFilteredPlans(plans);
    }
  }, [searchTerm, plans]);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6366f1",
      confirmButtonText: "Yes, delete it!",
      background: "#ffffff",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/delete_plan/${id}`,
          { method: "DELETE" }
        );
        const data = await response.json();

        if (response.ok && data.success) {
          const updatedList = plans.filter((plan) => plan._id !== id);
          setPlans(updatedList);
          setFilteredPlans(updatedList);

          const totalPagesAfterDelete = Math.ceil(updatedList.length / plansPerPage);
          if (currentPage > totalPagesAfterDelete && totalPagesAfterDelete > 0) {
            setCurrentPage(totalPagesAfterDelete);
          } else if (totalPagesAfterDelete === 0) {
            setCurrentPage(1);
          }

          Swal.fire({
            title: "Deleted!",
            text: "Plan has been deleted.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: data.message || "Failed to delete plan.",
            icon: "error",
            confirmButtonColor: "#ef4444"
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({
          title: "Error!",
          text: "Server error while deleting.",
          icon: "error",
          confirmButtonColor: "#ef4444"
        });
      }
    }
  };

  const indexOfLast = currentPage * plansPerPage;
  const indexOfFirst = indexOfLast - plansPerPage;
  const currentPlans = filteredPlans.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredPlans.length / plansPerPage);

  const stats = {
    total: plans.length,
    categories: [...new Set(plans.map(plan => plan.category).filter(Boolean))].length
  };

  const renderPageNumbers = () => {
    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FaChartLine className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Business Plans
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Manage and view all business plans</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.location.reload()}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Plans</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-1">All business plans</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <FaChartLine className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Categories</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.categories}</p>
                <p className="text-xs text-gray-400 mt-1">Plan categories</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FiGrid className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Plans</p>
                <p className="text-3xl font-bold text-purple-600">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-1">Currently active</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <FiTrendingUp className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and View Toggle */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
          <div className="p-5">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by plan title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition ${
                    viewMode === 'grid'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FiGrid className="w-4 h-4" />
                  Grid View
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition ${
                    viewMode === 'list'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FiList className="w-4 h-4" />
                  List View
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Showing {filteredPlans.length > 0 ? indexOfFirst + 1 : 0} to{' '}
            {Math.min(indexOfLast, filteredPlans.length)} of {filteredPlans.length} plans
          </p>
        </div>

        {/* Plans Grid View */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-8">
            <AnimatePresence>
              {currentPlans.length === 0 ? (
                <div className="col-span-full text-center py-16">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                      <FaChartLine className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium text-lg">No plans found</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your search</p>
                  </div>
                </div>
              ) : (
                currentPlans.map((plan, index) => (
                  <motion.div
                    key={plan._id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.03 }}
                    className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="aspect-square overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100">
                      <img
                        src={plan.photo}
                        alt={plan.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-800 text-center line-clamp-1">
                        {plan.title}
                      </h3>
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button
                        onClick={() => handleDelete(plan._id)}
                        className="p-2 bg-red-500 rounded-lg text-white hover:bg-red-600 transition"
                        title="Delete Plan"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-1 bg-black/50 backdrop-blur-sm rounded-lg text-white text-xs">
                        #{indexOfFirst + index + 1}
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedImage(plan.photo)}
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                    >
                      <FaEye className="w-8 h-8 text-white" />
                    </button>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        ) : (
          /* List View */
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Image</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentPlans.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FaChartLine className="w-10 h-10 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium text-lg">No plans found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your search</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentPlans.map((plan, index) => (
                      <motion.tr
                        key={plan._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.03 }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {indexOfFirst + index + 1}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button onClick={() => setSelectedImage(plan.photo)}>
                            <img
                              src={plan.photo}
                              alt={plan.title}
                              className="w-12 h-12 rounded-lg object-cover border-2 border-indigo-200 hover:border-indigo-500 transition-all shadow-md"
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FaChartLine className="w-4 h-4 text-indigo-500" />
                            <span className="font-medium text-gray-800">{plan.title}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => setSelectedImage(plan.photo)}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition"
                              title="View Image"
                            >
                              <FaEye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(plan._id)}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600 transition"
                              title="Delete Plan"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {filteredPlans.length > plansPerPage && (
          <div className="flex justify-center items-center gap-2 flex-wrap">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-full bg-white shadow-md hover:shadow-lg disabled:opacity-50 transition"
            >
              <FaChevronLeft size={14} className="text-gray-600" />
            </button>
            {renderPageNumbers()}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-full bg-white shadow-md hover:shadow-lg disabled:opacity-50 transition"
            >
              <FaChevronRight size={14} className="text-gray-600" />
            </button>
          </div>
        )}
      </div>

      {/* Image Enlarged Modal */}
      <AnimatePresence>
        {selectedImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setSelectedImage(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 p-2 bg-white/20 rounded-full hover:bg-white/30 transition"
              >
                <IoMdClose className="text-white text-xl" />
              </button>
              <img
                src={selectedImage}
                alt="Plan Enlarged"
                className="max-w-[90vw] max-h-[90vh] rounded-2xl shadow-2xl border-4 border-white"
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlanList;